/**
 * Analytics: PostHog (events + session replay) + Microsoft Clarity (heatmaps)
 * + Meta Pixel (marketing) + Web Vitals (LCP/INP/CLS) reporting.
 *
 * PostHog/Clarity/Vitals are gated by the 'analytics' consent category.
 * Meta Pixel is gated by the 'marketing' consent category.
 * All keys are publishable.
 */
import { hasConsent, onConsentChange } from './consent';

const POSTHOG_KEY = 'phc_AfBeYiuHFEi9F6vwkrfgeBaTZrMoFsaKqvZBDEURedaZ';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const CLARITY_ID = 'wzlz18d7tg';
const META_PIXEL_ID = '1321042763535103';
const PRODUCTION_HOSTS = new Set(['buxapbloom.com', 'www.buxapbloom.com']);

let posthogInitialized = false;
let clarityInitialized = false;
let vitalsInitialized = false;
let metaPixelInitialized = false;

function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false;
  return PRODUCTION_HOSTS.has(window.location.hostname.toLowerCase());
}


// PostHog SDK is dynamically imported so it does not bloat the initial bundle.
type PostHog = typeof import('posthog-js').default;
let posthogRef: PostHog | null = null;

async function initPostHog() {
  if (posthogInitialized || typeof window === 'undefined') return;
  posthogInitialized = true;
  try {
    const mod = await import('posthog-js');
    const ph = mod.default;
    ph.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      session_recording: { maskAllInputs: true },
      persistence: 'localStorage+cookie',
      disable_session_recording: false,
    });
    posthogRef = ph;
  } catch {
    posthogInitialized = false;
  }
}

function initClarity() {
  if (clarityInitialized || typeof window === 'undefined') return;
  clarityInitialized = true;
  try {
    // Official Clarity snippet
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function (...args: unknown[]) { (c[a].q = c[a].q || []).push(args); };
      const t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.src = 'https://www.clarity.ms/tag/' + i;
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    })(window, document, 'clarity', 'script', CLARITY_ID);
  } catch {
    clarityInitialized = false;
  }
}

async function initWebVitals() {
  if (vitalsInitialized || typeof window === 'undefined') return;
  vitalsInitialized = true;
  try {
    const { onLCP, onINP, onCLS, onTTFB, onFCP } = await import('web-vitals');
    const report = (metric: { name: string; value: number; rating: string; id: string }) => {
      track('web_vital', {
        metric: metric.name,
        value: Math.round(metric.value * 1000) / 1000,
        rating: metric.rating,
        metric_id: metric.id,
        path: window.location.pathname,
      });
    };
    onLCP(report);
    onINP(report);
    onCLS(report);
    onTTFB(report);
    onFCP(report);
  } catch {
    vitalsInitialized = false;
  }
}

/** Inject the Meta Pixel base script. PageView is fired by SPA route tracking. */
function initMetaPixel() {
  if (metaPixelInitialized || typeof window === 'undefined') return;
  metaPixelInitialized = true;
  try {
    /* eslint-disable */
    // @ts-ignore - official Meta Pixel snippet
    !(function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    fbqCall('init', META_PIXEL_ID);
  } catch {
    metaPixelInitialized = false;
  }
}

/** Low-level fbq call — no-ops if pixel not loaded. */
function fbqCall(...args: unknown[]) {
  try {
    const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
    fbq?.(...args);
  } catch {
    /* noop */
  }
}

/** Track a Meta Pixel standard event (no-op without marketing consent / pixel). */
export function fbqTrack(event: string, params: Record<string, unknown> = {}) {
  if (!isProductionHost()) return;
  fbqCall('track', event, params);
}

/** Boot all trackers if consent is granted; otherwise wait for consent change. */
export function initAnalytics() {
  if (!isProductionHost()) return;
  const boot = () => {
    if (hasConsent('analytics')) {
      initPostHog();
      initClarity();
      initWebVitals();
    }
    if (hasConsent('marketing')) {
      initMetaPixel();
    }
  };
  boot();
  onConsentChange(boot);
}

/** Fire Meta Pixel PageView on SPA route changes. Safe pre-init. */
export function trackPageView() {
  if (!isProductionHost() || window.location.pathname.startsWith('/admin')) return;
  fbqCall('track', 'PageView');
}


/** Generic event tracker — safe to call before init; will no-op silently. */
export function track(event: string, props: Record<string, unknown> = {}) {
  if (!isProductionHost()) return;
  try {
    posthogRef?.capture(event, props);
  } catch {
    /* noop */
  }
}

/** Identify a user (call on login). */
export function identify(userId: string, traits: Record<string, unknown> = {}) {
  try {
    posthogRef?.identify(userId, traits);
  } catch {
    /* noop */
  }
}

/** Reset user identity (call on logout). */
export function resetIdentity() {
  try {
    posthogRef?.reset();
  } catch {
    /* noop */
  }
}

// ---- Ecommerce events (GA4-style names for portability) ----

export interface EcomItem {
  item_id: string;
  item_name: string;
  price?: number;
  currency?: string;
  quantity?: number;
  variant?: string;
  category?: string;
}

export function trackViewItem(item: EcomItem) {
  const value = (item.price ?? 0) * (item.quantity ?? 1);
  track('view_item', { ...item, value });
  fbqTrack('ViewContent', {
    content_ids: [item.item_id],
    content_name: item.item_name,
    content_type: 'product',
    content_category: item.category,
    value,
    currency: item.currency ?? 'USD',
  });
}

export function trackAddToCart(item: EcomItem) {
  const value = (item.price ?? 0) * (item.quantity ?? 1);
  track('add_to_cart', { ...item, value });
  fbqTrack('AddToCart', {
    content_ids: [item.item_id],
    content_name: item.item_name,
    content_type: 'product',
    value,
    currency: item.currency ?? 'USD',
  });
}

export function trackBeginCheckout(items: EcomItem[], value: number, currency = 'USD') {
  track('begin_checkout', { items, value, currency, num_items: items.length });
  fbqTrack('InitiateCheckout', {
    content_ids: items.map((i) => i.item_id),
    contents: items.map((i) => ({ id: i.item_id, quantity: i.quantity ?? 1 })),
    content_type: 'product',
    num_items: items.reduce((s, i) => s + (i.quantity ?? 1), 0),
    value,
    currency,
  });
}

export function trackPurchase(
  orderId: string,
  items: EcomItem[],
  value: number,
  currency = 'USD',
) {
  track('purchase', { transaction_id: orderId, items, value, currency });
  fbqTrack('Purchase', {
    content_ids: items.map((i) => i.item_id),
    contents: items.map((i) => ({ id: i.item_id, quantity: i.quantity ?? 1 })),
    content_type: 'product',
    num_items: items.reduce((s, i) => s + (i.quantity ?? 1), 0),
    value,
    currency,
    order_id: orderId,
  });
}
